import {
  ChangeDetectorRef,
  Component,
  Injector,
  Input,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';

import { WidgetBase } from '../../../models/widget-base';
import { FbEmrInsuranceInfoWidget } from '../../../models/form-widget-emr-insurance-info.model';
import { RxButtonComponent } from '@nrwl/rx-button';
import { RxIconComponent } from 'libs/rx-ui/src/lib/rx-icon/rx-icon.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatNativeDateModule } from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { NgSelectModule } from '@ng-select/ng-select';
import { WidgetApiService } from '../../../rx-form-presenter/services/widget-api.service';
import { DateValidationService } from '../../../services/date-validation.service';
import { RxStatesComponent } from '../../../helpers/rx-states/rx-states.component';
import { parseLocalDateString } from '../../../helpers/date-utils';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';
import { buildInsuranceFieldKey } from '../../../amendments/amendment-fields.emr';

@Component({
  selector: 'app-fb-emr-insurance-info',
  templateUrl: './fb-emr-insurance-info.component.html',
  styleUrls: ['./fb-emr-insurance-info.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RxButtonComponent,
    RxIconComponent,
    MatDatepickerModule,
    MatDividerModule,
    MatIconModule,
    MatNativeDateModule,
    MatInputModule,
    MatFormFieldModule,
    MatSelectModule,
    NgSelectModule,
    RxStatesComponent,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
})
export class FbEmrInsuranceInfoComponent extends WidgetBase implements OnInit, OnDestroy {
  @Output() onDiscrepanciesDetected = new EventEmitter<any[]>();

  private readonly FIELD_MAPPINGS = {
    // API field names
    customerToApi: {
      ph_first_name: 'PolicyHolderFirstName',
      ph_last_name: 'PolicyHolderLastName',
      ph_gender: 'PolicyHolderGender',
      ph_birth_date: 'PolicyHolderBirthday',
      ph_relationship: 'PolicyHolderClientRelation',
      ph_employer: 'PolicyHolderEmployerName',
      ph_address1: 'PolicyHolderAddressOne',
      ph_address2: 'PolicyHolderAddressTwo',
      ph_city: 'PolicyHolderCity',
      ph_state: 'PolicyHolderState',
      ph_postal: 'PolicyHolderPostal',
      ph_phone_number: 'PolicyHolderPhoneNumber',
      policy_number: 'PolicyNumber',
      group_number: 'GroupNumber',
      effective_date: 'EffectiveDate',
      expiration_date: 'ExpirationDate',
      company_code: 'CompanyCode',
      address1: 'CompanyAddressOne',
      address2: 'CompanyAddressTwo',
      city: 'CompanyCity',
      state: 'CompanyState',
      postal: 'CompanyPostal',
      phone_number: 'CompanyPhoneNumber',
    },
    // Discrepancy reporting field names
    fieldLabels: {
      company_code: 'Plan Name',
      effective_date: 'Effective Date',
      expiration_date: 'Expiration Date',
      policy_number: 'Policy Number',
      group_number: 'Group Number',
      address1: 'Address 1',
      address2: 'Address 2',
      city: 'City',
      state: 'State',
      postal: 'ZIP',
      phone_number: 'Phone',
      ph_relationship: 'Relationship to Client',
      ph_employer: 'Employer Name',
      ph_first_name: 'First Name',
      ph_last_name: 'Last Name',
      ph_gender: 'Gender',
      ph_birth_date: 'Date of Birth',
      ph_address1: 'Address 1',
      ph_address2: 'Address 2',
      ph_city: 'City',
      ph_state: 'State',
      ph_postal: 'Zip/Postal',
      ph_phone_number: 'Phone Number',
    },
  } as const;

  @Input()
  public override set payload(value) {
    this._payload = value;
    if (value && !this.isDesign) {
      this.initializePayloadStructure();
    }

    this.ready.next(true);
  }
  public override get payload() {
    return this._payload;
  }

  private _lastLoadedPatientId: number | null = null;
  private _isLoadingInsuranceData = false;
  private _dataLoadedOnce = false;
  private pneInsuranceData: any[] = [];
  private conflictingFields: Map<string, Set<string>> = new Map();
  private conflictDetails: Map<string, Map<string, { saved: any; current: any }>> = new Map();
  public discrepancies: any[] = [];

  @Input()
  public override set patient(value) {
    this._patient = value;

    if (value?.remote_id && !this.isPreview && !this.isPrinting && !this.isInstanceDesign) {
      if (this._lastLoadedPatientId !== value.remote_id && !this._isLoadingInsuranceData) {
        this._lastLoadedPatientId = value.remote_id;
        this.loadInsuranceData(value.remote_id);
      }
    }
  }
  public override get patient() {
    return this._patient;
  }

  @Input()
  public override set isPreview(value: boolean) {
    this._isPreview = value;
  }
  public override get isPreview() {
    return this._isPreview;
  }

  @Input()
  public override set isPrinting(value: boolean) {
    this._isPrinting = value;
  }
  public override get isPrinting() {
    return this._isPrinting;
  }

  @Input()
  public set savedFormData(value: any) {
    this._savedFormData = value;
  }
  public get savedFormData(): any {
    return this._savedFormData;
  }

  private _savedFormData: any;

  public originalSections: any[] = [];
  public primaryInsuranceTemplate: any = null;
  private insuranceTemplateSnapshot: any = null;

  private defaultContainerList: { source: any; length: number; result: any[] } | null = null;
  private readonly defaultEntryLists = new WeakMap<object, { first: any; result: any[] }>();
  private readonly defaultStateFields = new WeakMap<object, any>();

  /**
   * True while a patient's own plan is being customized. The widget then draws the primary
   * insurance as the template defines it (no values, no added entries) and never reads or writes
   * the saved insurances, so they are stored back unchanged.
   */
  public get isInstanceDesign(): boolean {
    return !!this.isDesign && !!this.entity;
  }

  public getContainers(): any[] {
    const containers = this.payload?.properties?.items?.value;
    if (!this.isInstanceDesign || !Array.isArray(containers)) {
      return containers;
    }

    const cached = this.defaultContainerList;
    if (cached && cached.source === containers && cached.length === containers.length) {
      return cached.result;
    }

    const primary = containers.find((container: any) => container?.name === 'primary-insurance');
    const result = primary ? [primary] : [];
    this.defaultContainerList = { source: containers, length: containers.length, result };
    return result;
  }

  public getEntries(container: any): any[] {
    if (!this.isInstanceDesign) {
      return container.items;
    }

    const first = Array.isArray(container.items) ? container.items[0] : undefined;
    const cached = this.defaultEntryLists.get(container);
    if (cached && cached.first === first) {
      return cached.result;
    }

    const result = first ? [first] : [];
    this.defaultEntryLists.set(container, { first, result });
    return result;
  }

  public fieldValue(field: any): any {
    return this.isInstanceDesign ? '' : field.value;
  }

  public setFieldValue(field: any, value: any): void {
    if (this.isInstanceDesign) {
      return;
    }
    field.value = value;
  }

  public getDisplayField(field: any): any {
    if (!this.isInstanceDesign) {
      return field;
    }

    let copy = this.defaultStateFields.get(field);
    if (!copy) {
      copy = { ...field, value: '' };
      this.defaultStateFields.set(field, copy);
    }
    return copy;
  }

  public isConflicting(field: any): boolean {
    return !this.isInstanceDesign && !!field?.hasConflict;
  }
  private _insurancePayers: any[] = [];
  public customerInsurances: any[] = [];
  public isLoadingInsurancePlans = false;
  public hasPrimaryInsuranceReplacement = false;

  private currentPage = 0;
  private pageSize = 100;
  public hasMorePages = true;
  public isLoadingMore = false;
  public searchTerm = '';
  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  public get insurancePayers(): any[] {
    return this._insurancePayers || [];
  }

  public set insurancePayers(value: any[]) {
    this._insurancePayers = value;
  }
  public preventionLevel: any = {
    1: 'primary-insurance',
    2: 'secondary-insurance',
    3: 'tertiary-insurance',
  };

  constructor(
    private injector: Injector,
    private readonly widgetApiService: WidgetApiService,
    private readonly _cd: ChangeDetectorRef,
    private readonly dateValidationService: DateValidationService,
  ) {
    super(injector);
  }

  ngOnInit() {
    if (!this.isDesign && !this.isPreview && this.widgetApiService) {
      if (!this._dataLoadedOnce) {
        this.loadInsurancePlansPage(1);
        this._dataLoadedOnce = true;
      }

      this.searchSubject
        .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
        .subscribe((searchTerm) => {
          this.searchTerm = searchTerm;
          this.currentPage = 0;
          this.hasMorePages = true;

          this.clearSearchResults();
          this.loadInsurancePlansPage(1, searchTerm || undefined);
        });
    }
  }

  override ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private ensureSavedPlansInList(): void {
    const items = this.payload?.properties?.items?.value;
    if (!items) return;

    const savedPlans: any[] = [];
    const payerIdsToFetch: string[] = [];

    items.forEach((container: any) => {
      if (!container.items) return;

      container.items.forEach((insuranceEntry: any) => {
        if (!insuranceEntry.items) return;

        insuranceEntry.items.forEach((group: any) => {
          if (!group.items) return;

          group.items.forEach((field: any) => {
            if (field.name === 'company_code' && field.value) {
              if (typeof field.value === 'object') {
                const plan = field.value;
                if (!savedPlans.some((p) => p.PayerId === plan.PayerId)) {
                  savedPlans.push({
                    ...plan,
                    _isFromSavedData: true,
                  });
                }
              } else if (typeof field.value === 'string' && field.value.trim()) {
                const payerId = field.value.trim();
                if (
                  !payerIdsToFetch.includes(payerId) &&
                  !savedPlans.some((p) => p.PayerId === payerId)
                ) {
                  payerIdsToFetch.push(payerId);
                }
              }
            }
          });
        });
      });
    });

    if (savedPlans.length > 0) {
      this._insurancePayers = [...savedPlans, ...this._insurancePayers];
    }

    if (payerIdsToFetch.length > 0) {
      this.fetchMissingPlansByPayerId(payerIdsToFetch);
    }
  }

  private fetchMissingPlansByPayerId(payerIds: string[]): void {
    payerIds.forEach((payerId) => {
      this.widgetApiService.getInsurancePlanByPayerId(payerId).subscribe({
        next: (plan) => {
          if (plan && !this._insurancePayers.some((p) => p.PayerId === plan.PayerId)) {
            this._insurancePayers = [
              {
                ...plan,
                _isFromPayerIdLookup: true,
              },
              ...this._insurancePayers,
            ];
            this._cd.markForCheck();
          }
        },
        error: (error) => {
          console.error(`Failed to fetch plan for PayerId ${payerId}:`, error);
        },
      });
    });
  }

  private clearSearchResults(): void {
    this.currentPage = 0;
    this.hasMorePages = true;

    this._insurancePayers = this._insurancePayers.filter(
      (plan) =>
        plan._isFromSavedData ||
        plan._isFromPayerIdLookup ||
        plan._isFromCustomerData ||
        !plan._isFromSearch,
    );
  }

  private loadInsurancePlansPage(page: number, searchTerm?: string): void {
    if (this.isLoadingMore || !this.hasMorePages) {
      return;
    }

    if (page === 1) {
      this.isLoadingInsurancePlans = true;
      this._insurancePayers = [];
    } else {
      this.isLoadingMore = true;
    }

    this.widgetApiService.getInsurancePlansPage(page, this.pageSize, searchTerm).subscribe({
      next: (response) => {
        const newPlans = response.data || [];

        if (page === 1) {
          if (searchTerm) {
            const savedPlans = this._insurancePayers.filter(
              (plan) =>
                plan._isFromSavedData || plan._isFromPayerIdLookup || plan._isFromCustomerData,
            );

            const markedSearchPlans = newPlans.map((plan) => ({
              ...plan,
              _isFromSearch: true,
            }));

            this._insurancePayers = [...savedPlans, ...markedSearchPlans];
          } else {
            const savedPlans = this._insurancePayers.filter(
              (plan) =>
                plan._isFromSavedData || plan._isFromPayerIdLookup || plan._isFromCustomerData,
            );

            const existingIds = new Set(savedPlans.map((p) => p.PayerId));
            const uniqueApiPlans = newPlans.filter((plan) => !existingIds.has(plan.PayerId));

            this._insurancePayers = [...savedPlans, ...uniqueApiPlans];
          }
        } else {
          const existingIds = new Set(this._insurancePayers.map((p) => p.PayerId));
          const uniqueNewPlans = newPlans
            .filter((plan) => !existingIds.has(plan.PayerId))
            .map((plan) => ({
              ...plan,
              _isFromSearch: searchTerm ? true : undefined,
            }));
          this._insurancePayers = [...this._insurancePayers, ...uniqueNewPlans];
        }

        this.currentPage = page;
        this.hasMorePages = response.hasMore;

        this.isLoadingInsurancePlans = false;
        this.isLoadingMore = false;

        this._cd.markForCheck();
        this._cd.detectChanges();

        if (page === 1 && this.pendingInsuranceData) {
          this.prefillInsuranceData(this.pendingInsuranceData);
          this.pendingInsuranceData = null;
        }

        if (page === 1 && !searchTerm) {
          this.ensureSavedPlansInList();
        }
      },
      error: (error) => {
        console.error(`Error loading insurance plans page ${page}:`, error);
        this.isLoadingInsurancePlans = false;
        this.isLoadingMore = false;
        this.hasMorePages = false;
        this._cd.markForCheck();
        this._cd.detectChanges();
      },
    });
  }

  private pendingInsuranceData: any[] | null = null;

  private initializePayloadStructure() {
    if (!this.payload) {
      this.payload = { properties: { items: { value: [] } } };
    }
    if (!this.payload.properties) {
      this.payload.properties = { items: { value: [] } };
    }
    if (!this.payload.properties.items) {
      this.payload.properties.items = { value: [] };
    }

    this.originalSections = this.payload.properties.items?.value || [];

    const primaryInsurance = this.originalSections.find(
      (item) => item.name === 'primary-insurance',
    );

    if (primaryInsurance && primaryInsurance.items && primaryInsurance.items.length > 0) {
      const templateSnapshot = this.deepCloneForMutation(primaryInsurance.items[0]);
      this.insuranceTemplateSnapshot = templateSnapshot;
      this.primaryInsuranceTemplate = this.deepCloneForMutation(templateSnapshot);
    }

    this.payload.properties.items.value = this.originalSections;
  }

  private loadInsuranceData(patientId: number) {
    if (this.isInstanceDesign || !this.widgetApiService || this.isPreview || this.isPrinting) {
      return;
    }

    this._isLoadingInsuranceData = true;

    this.widgetApiService.getCustomerInsurances({ CustomerId: patientId }).subscribe({
      next: (customerInsurances) => {
        const activeInsurances = customerInsurances?.filter((ins) => ins.IsActive === true) || [];
        const sortedInsurances = activeInsurances.sort(
          (a, b) => a.PreventionLevel - b.PreventionLevel,
        );
        const primary = sortedInsurances.filter((ins) => ins.PreventionLevel === 1);
        const secondary = sortedInsurances.filter((ins) => ins.PreventionLevel === 2);
        const tertiary = sortedInsurances.filter((ins) => ins.PreventionLevel === 3);

        this.customerInsurances = sortedInsurances;
        this.pneInsuranceData = sortedInsurances;
        this._isLoadingInsuranceData = false;

        if (this.insurancePayers && this.insurancePayers.length > 0) {
          this.prefillInsuranceData(sortedInsurances);
        } else {
          this.pendingInsuranceData = sortedInsurances;
        }

        this.detectDiscrepancies();
      },
      error: (error) => {
        console.error('Error loading insurance data:', error);
        this._isLoadingInsuranceData = false;
      },
    });
  }

  private addMissingInsurancePlans(insurances: any[]) {
    if (!insurances || insurances.length === 0) {
      return;
    }

    const customerPlansMap = new Map<string, string>();
    insurances.forEach((insurance) => {
      if (insurance.CompanyCode && insurance.CompanyName) {
        customerPlansMap.set(insurance.CompanyCode, insurance.CompanyName);
      }
    });

    const existingPlanIds = new Set(this._insurancePayers.map((p) => p.PayerId));
    const missingPlans: any[] = [];

    customerPlansMap.forEach((name, payerId) => {
      if (!existingPlanIds.has(payerId)) {
        missingPlans.push({
          PayerId: payerId,
          Name: name,
          InsurancePayerId: '',
          LastUpdateOn: new Date().toISOString(),
          _isFromCustomerData: true,
        });
      }
    });

    if (missingPlans.length > 0) {
      this._insurancePayers = [...missingPlans, ...this._insurancePayers];

      this._cd.markForCheck();
      this._cd.detectChanges();
    }
  }

  private prefillInsuranceData(insurances: any[]) {
    if (this.isInstanceDesign || !this.payload?.properties?.items?.value || !insurances?.length) {
      return;
    }

    const formStatus = this.entity?.status;
    const isFormIncomplete = formStatus !== undefined && formStatus < 5;
    const hasSavedInsuranceMetadata =
      this.entity?.metadata?.primary_insurance ||
      this.entity?.metadata?.secondary_insurance ||
      this.entity?.metadata?.tertiary_insurance;

    if (isFormIncomplete && hasSavedInsuranceMetadata) {
      const primaryInsuranceWithoutId =
        this.entity.metadata.primary_insurance && !this.entity.metadata.primary_insurance.id;

      if (primaryInsuranceWithoutId) {
        this.hasPrimaryInsuranceReplacement = true;
        this.prefillFromInsuranceMetadata(this.entity.metadata);
        return;
      }

      this.prefillFromInsuranceMetadata(this.entity.metadata);

      this.checkForAdditionalPNEInsurance(insurances);
      return;
    }

    const items = this.payload.properties.items.value;

    const activeInsurances = insurances.filter((insurance) => insurance.IsActive === true);

    this.addMissingInsurancePlans(activeInsurances);

    const insurancesByLevel: Map<number, any[]> = new Map();
    activeInsurances.forEach((insurance) => {
      if (!insurancesByLevel.has(insurance.PreventionLevel)) {
        insurancesByLevel.set(insurance.PreventionLevel, []);
      }
      insurancesByLevel.get(insurance.PreventionLevel)?.push(insurance);
    });

    insurancesByLevel.forEach((levelInsurances, preventionLevel) => {
      const containerName = this.preventionLevel[preventionLevel];
      if (!containerName) {
        return;
      }

      let container = items.find((item) => item.name === containerName);

      if (!container) {
        const isForm = this.entity && 'patient' in this.entity;
        const entityAsForm = isForm ? (this.entity as any) : null;
        const isClientLibraryForm = isForm && entityAsForm?.patient && this.entity?.status >= 2;
        const hasInsuranceMetadata =
          this.entity?.metadata &&
          (this.entity.metadata.primary_insurance ||
            this.entity.metadata.secondary_insurance ||
            this.entity.metadata.tertiary_insurance);

        if (isClientLibraryForm && hasInsuranceMetadata) {
          return;
        }

        const primaryContainer = items.find((item) => item.name === 'primary-insurance');
        if (!primaryContainer || !this.primaryInsuranceTemplate) {
          return;
        }

        container = {
          name: containerName,
          title: containerName.replace('-', ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
          value: true,
          visible: true,
          items: [],
        };
        items.push(container);
        this.sortInsuranceContainers();
      }

      if (!container.items) {
        container.items = [];
      }

      container.value = true;

      levelInsurances.forEach((insurance, index) => {
        const metadata = this.entity?.metadata;
        const containerName = this.preventionLevel[preventionLevel];

        if (metadata) {
          if (containerName === 'primary-insurance' && metadata.primary_insurance !== undefined) {
            if (
              !metadata.primary_insurance ||
              Object.keys(metadata.primary_insurance).length === 0
            ) {
              return;
            }
            if (
              metadata.primary_insurance &&
              String(metadata.primary_insurance.id) !== String(insurance.CustomerInsuranceId)
            ) {
              return;
            }
          } else if (
            containerName === 'secondary-insurance' &&
            metadata.secondary_insurance !== undefined
          ) {
            if (!metadata.secondary_insurance || metadata.secondary_insurance.length === 0) {
              return;
            }
            if (
              metadata.secondary_insurance &&
              !metadata.secondary_insurance.some(
                (ins) => String(ins.id) === String(insurance.CustomerInsuranceId),
              )
            ) {
              return;
            }
          } else if (
            containerName === 'tertiary-insurance' &&
            metadata.tertiary_insurance !== undefined
          ) {
            if (!metadata.tertiary_insurance || metadata.tertiary_insurance.length === 0) {
              return;
            }
            if (
              metadata.tertiary_insurance &&
              !metadata.tertiary_insurance.some(
                (ins) => String(ins.id) === String(insurance.CustomerInsuranceId),
              )
            ) {
              return;
            }
          }
        }

        let insuranceEntry;

        if (index < container.items.length) {
          insuranceEntry = container.items[index];
        } else {
          if (!this.primaryInsuranceTemplate) {
            return;
          }

          insuranceEntry = this.createCleanInsuranceTemplate();
          if (!insuranceEntry) {
            return;
          }
          container.items.push(insuranceEntry);
        }

        insuranceEntry.value = true;

        const levelName = containerName
          .replace('-insurance', '')
          .replace(/\b\w/g, (l) => l.toUpperCase());
        insuranceEntry.title = `${levelName} Insurance ${index + 1}`;

        insuranceEntry.insurance_id = insurance.CustomerInsuranceId;

        if (insuranceEntry.items && Array.isArray(insuranceEntry.items)) {
          insuranceEntry.items.forEach((group, groupIdx) => {
            if (group.value === false) {
              group.value = true;
            }
            if (!group.items) return;

            group.items.forEach((field) => {
              if (field.visible === false) {
                field.visible = true;
              }

              let remoteName = field.remote_name;

              if (!remoteName) {
                remoteName = this.FIELD_MAPPINGS.customerToApi[field.name];
              }

              if (!remoteName) {
                return;
              }

              const remoteValue = insurance[remoteName];
              switch (field.name) {
                case 'company_code': {
                  const existingPlan = this._insurancePayers.find((p) => p.PayerId === remoteValue);
                  if (existingPlan) {
                    field.value = existingPlan;
                  } else if (remoteValue && insurance.CompanyName) {
                    const tempPlan = {
                      PayerId: remoteValue,
                      Name: insurance.CompanyName,
                      _isFromSavedData: true,
                    };

                    this._insurancePayers = [tempPlan, ...this._insurancePayers];
                    field.value = tempPlan;
                  } else {
                    field.value = remoteValue || '';
                  }
                  break;
                }

                case 'effective_date':
                case 'expiration_date':
                case 'ph_birth_date':
                  field.value = this.formatDate(remoteValue);
                  break;

                case 'ph_relationship':
                  if (remoteValue) {
                    const normalizedValue = remoteValue
                      .toString()
                      .toLowerCase()
                      .replace(/\s+/g, '');
                    field.value = normalizedValue;
                  } else {
                    field.value = '';
                  }
                  break;

                case 'ph_gender':
                  field.value = remoteValue || '';
                  break;
                case 'state':
                case 'ph_state':
                  field.value = remoteValue?.toUpperCase() || '';
                  break;
                default:
                  field.value = remoteValue || '';
                  break;
              }
            });
          });
        }
      });
    });

    this._cd.markForCheck();
    this._cd.detectChanges();
  }

  private formatDate(dateValue: string | Date): string {
    if (!dateValue) return '';

    if (typeof dateValue === 'string' && this.dateValidationService.isInvalidNetDate(dateValue)) {
      return '';
    }

    const formattedDate = parseLocalDateString(dateValue);

    if (formattedDate) {
      const year = parseInt(formattedDate.split('-')[0], 10);
      if (year < 1910) {
        return '';
      }
    }

    return formattedDate;
  }

  /**
   * Check if a date string represents an invalid .NET date that should be ignored
   */

  private deepCloneForMutation(obj: any): any {
    if (obj === null || typeof obj !== 'object') {
      return obj;
    }

    if (obj instanceof Date) {
      return new Date(obj);
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.deepCloneForMutation(item));
    }

    const cloned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = this.deepCloneForMutation(obj[key]);
      }
    }
    return cloned;
  }

  private createCleanInsuranceTemplate(): any {
    const templateSource =
      this.insuranceTemplateSnapshot ??
      this.primaryInsuranceTemplate ??
      this.originalSections?.find((item) => item.name === 'primary-insurance')?.items?.[0];

    if (!templateSource) {
      return null;
    }

    const cleanTemplate = this.deepCloneForMutation(templateSource);

    this.clearInsuranceValues(cleanTemplate);

    return cleanTemplate;
  }

  private clearInsuranceValues(insuranceEntry: any): void {
    if (!insuranceEntry) return;

    delete insuranceEntry.insurance_id;

    if (insuranceEntry.items && Array.isArray(insuranceEntry.items)) {
      insuranceEntry.items.forEach((group: any) => {
        if (group.items && Array.isArray(group.items)) {
          group.items.forEach((field: any) => {
            const originalValue = field.value;

            if (field.type === 'dropdown') {
              field.value = null;
              field.selectedValue = null;
            } else {
              field.value = '';
            }

            delete field.defaultValue;
            delete field.initialValue;

            delete field.remote_value;
            delete field.pne_value;
            delete field.rawValue;
            delete field.formattedValue;
            delete field.displayValue;

            delete field.hasConflict;
            delete field.conflictTooltip;
          });
        }
      });
    }
  }

  override isValid() {
    if (!this.payload?.properties?.items?.value) return true;

    for (const container of this.payload.properties.items.value) {
      if (!container.items) continue;

      for (const insurance of container.items) {
        if (insurance.value === false) continue;

        if (insurance.items) {
          for (const section of insurance.items) {
            if (section.items && section.value !== false) {
              for (const field of section.items) {
                if (field.required && field.visible !== false) {
                  if (
                    !field.value ||
                    (typeof field.value === 'string' && field.value.trim() === '')
                  ) {
                    return false;
                  }
                }
              }
            }
          }
        }
      }
    }
    return true;
  }

  private checkForAdditionalPNEInsurance(insurances: any[]) {
    if (!insurances || insurances.length === 0) {
      return;
    }

    const isForm = this.entity && 'patient' in this.entity;
    const entityAsForm = isForm ? (this.entity as any) : null;
    const isClientLibraryForm = isForm && entityAsForm?.patient && this.entity?.status >= 2;
    const hasInsuranceMetadata =
      this.entity?.metadata &&
      (this.entity.metadata.primary_insurance ||
        this.entity.metadata.secondary_insurance ||
        this.entity.metadata.tertiary_insurance);

    if (isClientLibraryForm && hasInsuranceMetadata) {
      return;
    }

    const items = this.payload.properties.items.value;
    const activeInsurances = insurances.filter((insurance) => insurance.IsActive === true);
    const metadata = this.entity?.metadata;

    const tertiaryInsurances = activeInsurances.filter((ins) => ins.PreventionLevel === 3);

    if (tertiaryInsurances.length > 0) {
      let tertiaryContainer = items.find((item) => item.name === 'tertiary-insurance');

      if (!tertiaryContainer) {
        const primaryContainer = items.find((item) => item.name === 'primary-insurance');
        if (primaryContainer && this.primaryInsuranceTemplate) {
          tertiaryContainer = {
            name: 'tertiary-insurance',
            title: 'Tertiary Insurance',
            value: true,
            visible: true,
            items: [],
          };
          items.push(tertiaryContainer);
          this.sortInsuranceContainers();
        } else {
          return;
        }
      }

      const existingTertiaryIds = new Set<string>();
      if (metadata?.tertiary_insurance && Array.isArray(metadata.tertiary_insurance)) {
        metadata.tertiary_insurance.forEach((ins) => {
          if (ins.id) {
            existingTertiaryIds.add(String(ins.id));
          }
        });
      }

      tertiaryContainer.items.forEach((item) => {
        if (item.insurance_id) {
          existingTertiaryIds.add(String(item.insurance_id));
        }
      });

      tertiaryInsurances.forEach((insurance, index) => {
        if (existingTertiaryIds.has(String(insurance.CustomerInsuranceId))) {
          return;
        }

        if (metadata && metadata.tertiary_insurance !== undefined) {
          if (!metadata.tertiary_insurance || metadata.tertiary_insurance.length === 0) {
            return;
          }

          if (
            metadata.tertiary_insurance &&
            !metadata.tertiary_insurance.some(
              (ins) => String(ins.id) === String(insurance.CustomerInsuranceId),
            )
          ) {
            return;
          }
        }

        const existingEntry = tertiaryContainer.items.find(
          (item) => item.insurance_id === insurance.CustomerInsuranceId,
        );

        if (!existingEntry) {
          const insuranceEntry = this.createCleanInsuranceTemplate();
          if (!insuranceEntry) {
            return;
          }

          insuranceEntry.value = true;
          insuranceEntry.title = `Tertiary Insurance ${tertiaryContainer.items.length + 1}`;
          insuranceEntry.insurance_id = insurance.CustomerInsuranceId;

          this.prefillInsuranceFields(insuranceEntry, insurance);

          tertiaryContainer.items.push(insuranceEntry);
        }
      });

      if (tertiaryContainer) {
        tertiaryContainer.value = true;
        tertiaryContainer.visible = true;
      }
    }

    const secondaryInsurances = activeInsurances.filter((ins) => ins.PreventionLevel === 2);

    if (secondaryInsurances.length > 0) {
      let secondaryContainer = items.find((item) => item.name === 'secondary-insurance');

      if (!secondaryContainer) {
        const primaryContainer = items.find((item) => item.name === 'primary-insurance');
        if (primaryContainer && this.primaryInsuranceTemplate) {
          secondaryContainer = {
            name: 'secondary-insurance',
            title: 'Secondary Insurance',
            value: true,
            visible: true,
            items: [],
          };
          items.push(secondaryContainer);
          this.sortInsuranceContainers();
        } else {
          return;
        }
      }

      const existingSecondaryIds = new Set<string>();
      if (metadata?.secondary_insurance && Array.isArray(metadata.secondary_insurance)) {
        metadata.secondary_insurance.forEach((ins) => {
          if (ins.id) {
            existingSecondaryIds.add(String(ins.id));
          }
        });
      }

      secondaryContainer.items.forEach((item) => {
        if (item.insurance_id) {
          existingSecondaryIds.add(String(item.insurance_id));
        }
      });

      secondaryInsurances.forEach((insurance, insuranceIndex) => {
        if (existingSecondaryIds.has(String(insurance.CustomerInsuranceId))) {
          return;
        }

        if (metadata && metadata.secondary_insurance !== undefined) {
          if (!metadata.secondary_insurance || metadata.secondary_insurance.length === 0) {
            return;
          }

          if (
            metadata.secondary_insurance &&
            !metadata.secondary_insurance.some(
              (ins) => String(ins.id) === String(insurance.CustomerInsuranceId),
            )
          ) {
            return;
          }
        }

        const existingEntry = secondaryContainer.items.find(
          (item) => item.insurance_id === insurance.CustomerInsuranceId,
        );

        if (!existingEntry) {
          let insuranceEntry;
          let entryIndex = -1;

          for (let i = 0; i < secondaryContainer.items.length; i++) {
            const item = secondaryContainer.items[i];
            if (
              !item.insurance_id &&
              (!item.items ||
                item.items.every(
                  (group) =>
                    !group.items ||
                    group.items.every((field) => !field.value || field.value === ''),
                ))
            ) {
              insuranceEntry = item;
              entryIndex = i;
              break;
            }
          }

          if (!insuranceEntry) {
            insuranceEntry = this.createCleanInsuranceTemplate();
            if (!insuranceEntry) {
              return;
            }
            secondaryContainer.items.push(insuranceEntry);
            entryIndex = secondaryContainer.items.length - 1;
          }

          insuranceEntry.value = true;
          insuranceEntry.title = `Secondary Insurance ${entryIndex + 1}`;
          insuranceEntry.insurance_id = insurance.CustomerInsuranceId;

          this.prefillInsuranceFields(insuranceEntry, insurance);
        }
      });

      if (secondaryContainer) {
        secondaryContainer.value = true;
        secondaryContainer.visible = true;
      }
    }

    this.sortInsuranceContainers();

    this._cd.markForCheck();
    this._cd.detectChanges();

    setTimeout(() => {
      this._cd.markForCheck();
      this._cd.detectChanges();
    }, 100);
  }

  private prefillInsuranceFields(insuranceEntry: any, insurance: any) {
    if (!insuranceEntry.items || !Array.isArray(insuranceEntry.items)) {
      return;
    }

    insuranceEntry.items.forEach((group, groupIndex) => {
      if (group.value === false) {
        group.value = true;
      }
      if (!group.items) {
        return;
      }

      group.items.forEach((field) => {
        if (field.visible === false) {
          field.visible = true;
        }

        if (!field.properties) {
          field.properties = { value: { value: '' } };
        }
        if (!field.properties.value) {
          field.properties.value = { value: '' };
        }

        let remoteName = field.remote_name;

        if (remoteName && insurance[remoteName] !== undefined && insurance[remoteName] !== null) {
          const value = insurance[remoteName];

          if (remoteName.includes('Date') || remoteName.includes('Birthday')) {
            if (value && value !== '0001-01-01T00:00:00') {
              const date = new Date(value);
              field.value = this.formatDate(date);
            }
          } else if (field.name === 'company_code' && insurance.PayerId) {
            field.value = {
              PayerId: insurance.PayerId || insurance.CompanyCode,
              Name: insurance.CompanyName || insurance.Name,
            };
          } else {
            field.value = String(value);
          }
        }
      });
    });
  }

  private prefillFromInsuranceMetadata(metadata: any) {
    if (!this.payload?.properties?.items?.value || !metadata) {
      return;
    }

    const items = this.payload.properties.items.value;

    if (metadata.primary_insurance && Object.keys(metadata.primary_insurance).length > 0) {
      this.prefillInsuranceLevel('primary-insurance', [metadata.primary_insurance], items);
    }

    if (metadata.secondary_insurance && Array.isArray(metadata.secondary_insurance)) {
      this.prefillInsuranceLevel('secondary-insurance', metadata.secondary_insurance, items);
    }

    if (metadata.tertiary_insurance && Array.isArray(metadata.tertiary_insurance)) {
      this.prefillInsuranceLevel('tertiary-insurance', metadata.tertiary_insurance, items);
    }

    this.sortInsuranceContainers();
    this.ready.next(true);
  }

  private prefillInsuranceLevel(levelName: string, insuranceData: any[], items: any[]) {
    const container = items.find((item) => item.name === levelName);
    if (!container || !container.items) {
      return;
    }

    insuranceData.forEach((insurance, index) => {
      if (!insurance || Object.keys(insurance).length === 0) {
        return;
      }

      if (index < container.items.length) {
        const insuranceItem = container.items[index];
        if (insuranceItem) {
          this.prefillInsuranceItem(insuranceItem, insurance);
          insuranceItem.value = true;
        }
      }
    });
  }

  private prefillInsuranceItem(insuranceItem: any, insuranceData: any) {
    if (!insuranceItem.items || !Array.isArray(insuranceItem.items)) {
      return;
    }

    if (insuranceData.id) {
      insuranceItem.insurance_id = insuranceData.id;
    }

    const insuranceId = String(insuranceData.id || 'new');
    if (!this.conflictingFields.has(insuranceId)) {
      this.conflictingFields.set(insuranceId, new Set());
    }
    if (!this.conflictDetails.has(insuranceId)) {
      this.conflictDetails.set(insuranceId, new Map());
    }

    insuranceItem.items.forEach((group: any) => {
      if (group.items && Array.isArray(group.items)) {
        group.items.forEach((field: any) => {
          const metadataKey = field.name;
          if (metadataKey) {
            if (!field.remote_name && this.FIELD_MAPPINGS.customerToApi[metadataKey]) {
              field.remote_name = this.FIELD_MAPPINGS.customerToApi[metadataKey];
            }

            const metadataValue = insuranceData[metadataKey];

            const shouldUsePNE =
              (!metadataValue || metadataValue === '') &&
              field.remote_name &&
              this.pneInsuranceData;

            field.hasConflict = false;
            field.conflictTooltip = '';

            if (shouldUsePNE) {
              const matchingPNE = this.pneInsuranceData.find(
                (pne) => String(pne.CustomerInsuranceId) === String(insuranceData.id),
              );

              if (
                matchingPNE &&
                matchingPNE[field.remote_name] !== undefined &&
                matchingPNE[field.remote_name] !== null
              ) {
                const pneValue = matchingPNE[field.remote_name];
                if (field.type === 'date' || field.remote_name?.includes('Date')) {
                  field.value = this.formatDate(pneValue);
                } else {
                  field.value = pneValue;
                }
              } else {
                field.value = '';
              }
            } else if (metadataValue !== undefined) {
              if (field.remote_name && this.pneInsuranceData && insuranceData.id) {
                const matchingPNE = this.pneInsuranceData.find(
                  (pne) => String(pne.CustomerInsuranceId) === String(insuranceData.id),
                );

                if (matchingPNE && matchingPNE[field.remote_name] != null) {
                  const pneValue = matchingPNE[field.remote_name];
                  let formattedPNEValue = pneValue;
                  let tooltipValue = formattedPNEValue;

                  if (field.type === 'date' || field.remote_name?.includes('Date')) {
                    formattedPNEValue = this.formatDate(pneValue);
                    tooltipValue = formattedPNEValue;
                  }

                  if (metadataKey === 'company_code' && matchingPNE.CompanyName) {
                    tooltipValue = matchingPNE.CompanyName;
                  }

                  const normalizedMetadata = String(metadataValue || '').trim();
                  const normalizedPNE = String(formattedPNEValue || '').trim();

                  if (normalizedMetadata && normalizedPNE && normalizedMetadata !== normalizedPNE) {
                    const isStateMatch =
                      (metadataKey === 'state' || metadataKey === 'ph_state') &&
                      normalizedMetadata.toUpperCase() === normalizedPNE.toUpperCase();

                    if (!isStateMatch) {
                      field.hasConflict = true;
                      field.conflictTooltip = `PatientNow: ${tooltipValue}`;

                      const conflicts = this.conflictingFields.get(insuranceId) || new Set();
                      conflicts.add(metadataKey);
                      this.conflictingFields.set(insuranceId, conflicts);

                      const details = this.conflictDetails.get(insuranceId) || new Map();
                      details.set(metadataKey, {
                        saved: metadataValue,
                        current: formattedPNEValue,
                      });
                      this.conflictDetails.set(insuranceId, details);
                    }
                  }
                }
              }

              if (metadataKey === 'company_code') {
                field.value = {
                  PayerId: insuranceData[metadataKey],
                  Name: insuranceData?.company_name,
                };
              } else if (metadataKey === 'state' || metadataKey === 'ph_state') {
                field.value = insuranceData?.[metadataKey]?.toUpperCase();
              } else {
                field.value = insuranceData[metadataKey];
              }
            }
          }
        });
      }
    });
  }

  public markAsTouched() {}

  public getMetadata() {
    if (this.payload && this.payload.properties) {
      const widgetData = { ...this.payload };

      widgetData.properties = { ...widgetData.properties };
      widgetData.properties.insurancePayers = this.insurancePayers;

      const widget = new FbEmrInsuranceInfoWidget(widgetData);
      return widget.getMetadata();
    }

    return {
      primary_insurance: {},
      secondary_insurance: [],
      tertiary_insurance: [],
    };
  }

  public removeInsurance(containerName: string, index: number) {
    if (this.isInstanceDesign) {
      return;
    }

    const container = this.payload.properties.items.value.find(
      (c: any) => c.name === containerName,
    );

    if (container && container.items && container.items.length > index) {
      if (containerName === 'secondary-insurance' || containerName === 'tertiary-insurance') {
        container.items.splice(index, 1);

        if (container.items.length === 0) {
          container.value = false;
        }
      } else {
        this.setInsuranceEntryActive(container.items[index], false);

        const hasVisibleItems = container.items.some((item: any) => item.value !== false);
        if (!hasVisibleItems) {
          container.value = false;
        }
      }
    }

    this._cd.detectChanges();
  }

  private addInsurance(
    kind: 'secondary' | 'tertiary',
    sectionTitle: string,
    entryTitle: string,
  ): string | undefined {
    if (!this.primaryInsuranceTemplate) {
      return undefined;
    }

    let container = this.payload.properties.items.value.find(
      (i: any) => i.name === `${kind}-insurance`,
    );
    if (!container) {
      container = {
        title: sectionTitle,
        name: `${kind}-insurance`,
        value: true,
        visible: true,
        nested: true,
        toggleVisibility: true,
        items: [],
      };
      this.payload.properties.items.value.push(container);
      this.sortInsuranceContainers();
    } else {
      container.value = true;
    }

    const newEntry = this.createCleanInsuranceTemplate();
    if (!newEntry) return undefined;

    newEntry.title = entryTitle;
    newEntry.value = true;
    const insuranceId = `${kind}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    newEntry.insurance_id = insuranceId;

    container.items.push(newEntry);
    this._cd.detectChanges();

    return insuranceId;
  }

  private sortInsuranceContainers() {
    const items = this.payload.properties.items.value;
    const order = ['primary-insurance', 'secondary-insurance', 'tertiary-insurance'];

    items.sort((a: any, b: any) => {
      const aIndex = order.indexOf(a.name);
      const bIndex = order.indexOf(b.name);

      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;

      return aIndex - bIndex;
    });
  }

  private scrollToInsuranceEntry(insuranceId: string) {
    setTimeout(() => {
      const element = document.getElementById(`insurance-entry-${insuranceId}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  public hasPrimaryInsurance(): boolean {
    const primaryContainer = this.payload.properties.items.value.find(
      (c: any) => c.name === 'primary-insurance',
    );
    if (!primaryContainer) return false;

    return (
      primaryContainer.value === true &&
      primaryContainer.items &&
      primaryContainer.items.some((item: any) => item.value !== false)
    );
  }

  public addPrimaryInsurance() {
    if (this.isInstanceDesign) {
      return;
    }

    const primaryContainer = this.payload.properties.items.value.find(
      (c: any) => c.name === 'primary-insurance',
    );

    if (primaryContainer) {
      primaryContainer.value = true;
      primaryContainer.visible = true;

      if (primaryContainer.items && primaryContainer.items.length > 0) {
        this.setInsuranceEntryActive(primaryContainer.items[0], true);

        this.emptyInsuranceValues(primaryContainer.items[0]);

        const primaryInsuranceId = primaryContainer.items[0].insurance_id || 'primary-insurance-0';
        this.scrollToInsuranceEntry(primaryInsuranceId);
      }
    }

    this._cd.detectChanges();
  }

  public addSecondaryInsurance() {
    if (this.isInstanceDesign) {
      return;
    }

    const sectionTitle = 'Secondary Insurance';

    const container = this.payload.properties.items.value.find(
      (i: any) => i.name === 'secondary-insurance',
    );
    let count = 1;
    if (container && container.items) {
      count = container.items.filter((item: any) => item.value !== false).length + 1;
    }
    const entryTitle = `Secondary Insurance ${count}`;
    const insuranceId = this.addInsurance('secondary', sectionTitle, entryTitle);
    if (insuranceId) {
      this.scrollToInsuranceEntry(insuranceId);
    }
  }

  public addTertiaryInsurance() {
    if (this.isInstanceDesign) {
      return;
    }

    const sectionTitle = 'Tertiary Insurance';

    const container = this.payload.properties.items.value.find(
      (i: any) => i.name === 'tertiary-insurance',
    );
    let count = 1;
    if (container && container.items) {
      count = container.items.filter((item: any) => item.value !== false).length + 1;
    }
    const entryTitle = `Tertiary Insurance ${count}`;
    const insuranceId = this.addInsurance('tertiary', sectionTitle, entryTitle);
    if (insuranceId) {
      this.scrollToInsuranceEntry(insuranceId);
    }
  }

  private emptyInsuranceValues(insuranceSection: any) {
    if (insuranceSection.items && insuranceSection.items.length > 0) {
      insuranceSection.items.forEach((group: any) => {
        if (group.items && group.items.length > 0) {
          group.items.forEach((field: any) => {
            if (field.type === 'dropdown') {
              field.value = null;
            } else {
              field.value = '';
            }
            delete field.hasConflict;
            delete field.conflictTooltip;
          });
        }
      });
    }
  }

  private setInsuranceEntryActive(insuranceEntry: any, active: boolean) {
    if (!insuranceEntry) {
      return;
    }

    insuranceEntry.value = active;
    insuranceEntry.visible = active;

    if (Array.isArray(insuranceEntry.items)) {
      insuranceEntry.items.forEach((group: any) => {
        if (!group) {
          return;
        }

        group.value = active;
        group.visible = active;
      });
    }
  }

  getInsuranceTitle(insuranceContainer: any): string {
    return insuranceContainer.title || '';
  }

  getGroupTitle(group: any): string {
    return group.title || '';
  }

  getColumnClass(index: number, field: any): string {
    const positionClass = index % 2 === 0 ? 'field-left' : 'field-right';
    const fullRowClass = field?.fullRow ? 'full-row' : '';
    return `${positionClass} ${fullRowClass}`.trim();
  }

  public onInsuranceCompanyChange(selectedPlan: any, field: any) {
    if (this.isInstanceDesign) {
      return;
    }

    field.value = selectedPlan;
    this.clearScribeSuggestionMarker(field);
    this.detectDiscrepancies();
  }

  public onFieldValueChange(field?: any) {
    if (this.isInstanceDesign) {
      return;
    }

    this.clearScribeSuggestionMarker(field);
    this.detectDiscrepancies();
  }

  public compareInsurancePlans(plan1: any, plan2: any): boolean {
    if (!plan1 || !plan2) return false;

    const payerId1 = typeof plan1 === 'string' ? plan1 : plan1.PayerId;
    const payerId2 = typeof plan2 === 'string' ? plan2 : plan2.PayerId;

    return payerId1 === payerId2;
  }

  public trackByPayerId(index: number, item: any): any {
    return item?.PayerId || index;
  }

  public trackByInsuranceIndex(index: number, item: any): any {
    return item?.insurance_id || `${index}_${item?.title}_${item?.value}`;
  }

  public onScrollToEnd(): void {
    if (this.hasMorePages && !this.isLoadingMore) {
      this.loadInsurancePlansPage(this.currentPage + 1, this.searchTerm || undefined);
    }
  }

  public onDropdownOpen(): void {
    if (this._insurancePayers.length === 0 && !this.isLoadingInsurancePlans) {
      this.loadInsurancePlansPage(1);
    }
  }

  public onSearch(event: { term: string; items: any[] }): void {
    const newSearchTerm = event.term || '';
    this.searchSubject.next(newSearchTerm);
  }

  public getInsurancePlanOptionsSnapshot(): Array<{ PayerId: string; Name: string }> {
    return this.insurancePayers
      .map((plan) => ({
        PayerId: `${plan?.PayerId ?? ''}`.trim(),
        Name: `${plan?.Name ?? ''}`.trim(),
      }))
      .filter((plan) => !!plan.PayerId && !!plan.Name);
  }

  public customSearchFn(term: string, item: any): boolean {
    return true;
  }

  public highlightMatch(text: string, searchTerm: string): string {
    if (!searchTerm || !text) {
      return text;
    }

    const regex = new RegExp(`(${searchTerm})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
  }

  isInsuranceContainer(item: any): boolean {
    return item.name?.includes('insurance');
  }

  getInsuranceType(item: any): string {
    if (item.name?.includes('primary')) return 'primary';
    if (item.name?.includes('secondary')) return 'secondary';
    if (item.name?.includes('tertiary')) return 'tertiary';
    return 'other';
  }

  toggleTooltip(event: MouseEvent): void {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    target.classList.toggle('show-tooltip');

    setTimeout(() => {
      target.classList.remove('show-tooltip');
    }, 3000);
  }

  /**
   * Detect discrepancies between saved insurance data and current PNE data
   * This method runs internally within the widget after data is loaded
   *
   * Business Rules:
   * - Account Library (Templates): No discrepancy detection, just prefill
   * - Client Library (Forms): Full discrepancy detection including:
   *   1. Presence/absence of Primary, Secondary, Tertiary on either side
   *   2. Value comparison when both have the same ID
   */
  private detectDiscrepancies(): void {
    if (this.isInstanceDesign) {
      return;
    }

    this.discrepancies = [];
    this.conflictingFields.clear();
    this.conflictDetails.clear();

    const isForm = this.entity && 'patient' in this.entity;
    const entityAsForm = isForm ? (this.entity as any) : null;

    const isTemplate = entityAsForm?.template && !entityAsForm?.patient;
    if (isTemplate) {
      return;
    }

    const isClientLibraryForm = isForm && entityAsForm?.patient && this.entity?.status >= 1;

    if (!isClientLibraryForm) {
      return;
    }

    if (!this.pneInsuranceData) {
      this.checkForMissingPNEInsurance();
      return;
    }

    this.clearAllConflictIndicators();

    const formStatus = this.entity?.status;
    const isFormIncomplete = formStatus !== undefined && formStatus < 5;

    const hasInsuranceMetadata =
      this.entity?.metadata &&
      (this.entity.metadata.primary_insurance !== undefined ||
        this.entity.metadata.secondary_insurance !== undefined ||
        this.entity.metadata.tertiary_insurance !== undefined);

    if (isFormIncomplete && hasInsuranceMetadata) {
      this.detectDiscrepanciesFromMetadata();
    }

    this.updateUIWithDiscrepancies();

    if (this.discrepancies.length > 0) {
      this.onDiscrepanciesDetected.emit(this.discrepancies);
    }
  }

  private checkForMissingPNEInsurance(): void {
    const metadata = this.entity?.metadata;

    if (metadata) {
      if (metadata.primary_insurance && Object.keys(metadata.primary_insurance).length > 0) {
        this.discrepancies.push({
          insuranceName: 'Primary Insurance',
          field: 'Presence',
          saved: 'Present',
          current: 'No PNE data available',
          type: 'presence',
        });
      }

      if (metadata.secondary_insurance && metadata.secondary_insurance.length > 0) {
        this.discrepancies.push({
          insuranceName: 'Secondary Insurance',
          field: 'Presence',
          saved: `${metadata.secondary_insurance.length} insurance(s)`,
          current: 'No PNE data available',
          type: 'presence',
        });
      }

      if (metadata.tertiary_insurance && metadata.tertiary_insurance.length > 0) {
        this.discrepancies.push({
          insuranceName: 'Tertiary Insurance',
          field: 'Presence',
          saved: `${metadata.tertiary_insurance.length} insurance(s)`,
          current: 'No PNE data available',
          type: 'presence',
        });
      }
    }
  }

  private clearAllConflictIndicators(): void {
    if (!this.payload?.properties?.items?.value) return;

    this.payload.properties.items.value.forEach((container: any) => {
      if (container.items && Array.isArray(container.items)) {
        container.items.forEach((insuranceEntry: any) => {
          if (insuranceEntry.items && Array.isArray(insuranceEntry.items)) {
            insuranceEntry.items.forEach((group: any) => {
              if (group.items && Array.isArray(group.items)) {
                group.items.forEach((field: any) => {
                  field.hasConflict = false;
                  field.conflictTooltip = '';
                });
              }
            });
          }
        });
      }
    });
  }

  private detectDiscrepanciesFromMetadata(): void {
    const metadata = this.entity.metadata;
    const primaryPNE = this.pneInsuranceData.find((ins) => ins.PreventionLevel === 1);
    const secondaryPNE = this.pneInsuranceData.filter((ins) => ins.PreventionLevel === 2);
    const tertiaryPNE = this.pneInsuranceData.filter((ins) => ins.PreventionLevel === 3);

    const hasSavedPrimary =
      metadata.primary_insurance && Object.keys(metadata.primary_insurance).length > 0;
    const hasPNEPrimary = !!primaryPNE;

    if (hasSavedPrimary && !hasPNEPrimary) {
      this.discrepancies.push({
        insuranceName: 'Primary Insurance',
        field: 'Presence',
        saved: 'Present',
        current: 'Absent',
        type: 'presence',
      });
    } else if (!hasSavedPrimary && hasPNEPrimary) {
      this.discrepancies.push({
        insuranceName: 'Primary Insurance',
        field: 'Presence',
        saved: 'Absent',
        current: 'Present',
        type: 'presence',
      });
    } else if (hasSavedPrimary && hasPNEPrimary) {
      this.compareInsuranceWithPNE(metadata.primary_insurance, primaryPNE, 'Primary Insurance');
    }

    const hasSavedSecondary =
      metadata.secondary_insurance && metadata.secondary_insurance.length > 0;
    const hasPNESecondary = secondaryPNE.length > 0;

    if (hasSavedSecondary && !hasPNESecondary) {
      this.discrepancies.push({
        insuranceName: 'Secondary Insurance',
        field: 'Presence',
        saved: `${metadata.secondary_insurance.length} insurance(s)`,
        current: 'None',
        type: 'presence',
      });
    } else if (!hasSavedSecondary && hasPNESecondary) {
      this.discrepancies.push({
        insuranceName: 'Secondary Insurance',
        field: 'Presence',
        saved: 'None',
        current: `${secondaryPNE.length} insurance(s)`,
        type: 'presence',
      });
    } else if (hasSavedSecondary && hasPNESecondary) {
      if (metadata.secondary_insurance.length !== secondaryPNE.length) {
        this.discrepancies.push({
          insuranceName: 'Secondary Insurance',
          field: 'Count',
          saved: `${metadata.secondary_insurance.length} insurance(s)`,
          current: `${secondaryPNE.length} insurance(s)`,
          type: 'count',
        });
      }

      metadata.secondary_insurance.forEach((savedIns, index) => {
        if (savedIns.id) {
          const pneIns = secondaryPNE.find(
            (ins) => String(ins.CustomerInsuranceId) === String(savedIns.id),
          );
          if (pneIns) {
            this.compareInsuranceWithPNE(savedIns, pneIns, `Secondary Insurance ${index + 1}`);
          } else {
            this.discrepancies.push({
              insuranceName: `Secondary Insurance ${index + 1}`,
              field: 'Existence',
              saved: `ID: ${savedIns.id}`,
              current: 'Not found in PatientNow',
              type: 'existence',
            });
          }
        }
      });
    }

    const hasSavedTertiary = metadata.tertiary_insurance && metadata.tertiary_insurance.length > 0;
    const hasPNETertiary = tertiaryPNE.length > 0;

    if (hasSavedTertiary && !hasPNETertiary) {
      this.discrepancies.push({
        insuranceName: 'Tertiary Insurance',
        field: 'Presence',
        saved: `${metadata.tertiary_insurance.length} insurance(s)`,
        current: 'None',
        type: 'presence',
      });
    } else if (!hasSavedTertiary && hasPNETertiary) {
      this.discrepancies.push({
        insuranceName: 'Tertiary Insurance',
        field: 'Presence',
        saved: 'None',
        current: `${tertiaryPNE.length} insurance(s)`,
        type: 'presence',
      });
    } else if (hasSavedTertiary && hasPNETertiary) {
      if (metadata.tertiary_insurance.length !== tertiaryPNE.length) {
        this.discrepancies.push({
          insuranceName: 'Tertiary Insurance',
          field: 'Count',
          saved: `${metadata.tertiary_insurance.length} insurance(s)`,
          current: `${tertiaryPNE.length} insurance(s)`,
          type: 'count',
        });
      }

      metadata.tertiary_insurance.forEach((savedIns, index) => {
        if (savedIns.id) {
          const pneIns = tertiaryPNE.find(
            (ins) => String(ins.CustomerInsuranceId) === String(savedIns.id),
          );
          if (pneIns) {
            this.compareInsuranceWithPNE(savedIns, pneIns, `Tertiary Insurance ${index + 1}`);
          } else {
            this.discrepancies.push({
              insuranceName: `Tertiary Insurance ${index + 1}`,
              field: 'Existence',
              saved: `ID: ${savedIns.id}`,
              current: 'Not found in PatientNow',
              type: 'existence',
            });
          }
        }
      });
    }
  }

  private extractSavedInsuranceValues(insuranceEntry: any): any {
    const values: any = {};

    if (insuranceEntry.items && Array.isArray(insuranceEntry.items)) {
      insuranceEntry.items.forEach((group: any) => {
        if (group.items && Array.isArray(group.items)) {
          group.items.forEach((field: any) => {
            if (field.name) {
              values[field.name] = field.value;
            }
          });
        }
      });
    }

    return values;
  }

  private compareInsuranceWithPNE(
    savedInsurance: any,
    pneInsurance: any,
    insuranceName: string,
  ): void {
    const insuranceId = String(savedInsurance.id || pneInsurance.CustomerInsuranceId || 'unknown');

    Object.keys(this.FIELD_MAPPINGS.customerToApi).forEach((fieldName) => {
      const savedValue = savedInsurance[fieldName];
      const pneField = this.FIELD_MAPPINGS.customerToApi[fieldName];
      const pneValue = pneInsurance[pneField];

      if (savedValue && pneValue) {
        const normalizedSaved = this.normalizeValue(fieldName, savedValue);
        const normalizedPNE = this.normalizeValue(fieldName, pneValue);

        if (normalizedSaved !== normalizedPNE && normalizedSaved && normalizedPNE) {
          if (!this.conflictingFields.has(insuranceId)) {
            this.conflictingFields.set(insuranceId, new Set());
          }
          this.conflictingFields.get(insuranceId)?.add(fieldName);

          if (!this.conflictDetails.has(insuranceId)) {
            this.conflictDetails.set(insuranceId, new Map());
          }
          this.conflictDetails.get(insuranceId)?.set(fieldName, {
            saved: savedValue,
            current: pneValue,
          });

          this.discrepancies.push({
            insuranceName,
            field: this.getFieldLabel(fieldName),
            saved: normalizedSaved,
            current: normalizedPNE,
          });
        }
      }
    });
  }

  private compareInsuranceValues(
    savedValues: any,
    pneInsurance: any,
    insuranceName: string,
    insuranceId: string,
  ): void {
    Object.keys(this.FIELD_MAPPINGS.customerToApi).forEach((fieldName) => {
      let savedValue = savedValues[fieldName];
      const pneField = this.FIELD_MAPPINGS.customerToApi[fieldName];
      const pneValue = pneInsurance[pneField];

      if (fieldName === 'company_code' && typeof savedValue === 'object') {
        savedValue = savedValue?.PayerId;
      }

      if (savedValue && pneValue) {
        const normalizedSaved = this.normalizeValue(fieldName, savedValue);
        const normalizedPNE = this.normalizeValue(fieldName, pneValue);

        if (normalizedSaved !== normalizedPNE && normalizedSaved && normalizedPNE) {
          if (!this.conflictingFields.has(insuranceId)) {
            this.conflictingFields.set(insuranceId, new Set());
          }
          this.conflictingFields.get(insuranceId)?.add(fieldName);

          if (!this.conflictDetails.has(insuranceId)) {
            this.conflictDetails.set(insuranceId, new Map());
          }
          this.conflictDetails.get(insuranceId)?.set(fieldName, {
            saved: savedValue,
            current: pneValue,
          });

          this.discrepancies.push({
            insuranceName,
            insuranceId,
            field: this.getFieldLabel(fieldName),
            saved: normalizedSaved,
            current: normalizedPNE,
          });
        }
      }
    });
  }

  private getFieldLabel(fieldName: string): string {
    return this.FIELD_MAPPINGS.fieldLabels[fieldName] || fieldName;
  }

  private normalizeValue(fieldName: string, value: any): string {
    if (!value) return '';

    if (
      fieldName.includes('date') ||
      fieldName === 'effective_date' ||
      fieldName === 'expiration_date'
    ) {
      return this.formatDate(value);
    }

    if (fieldName === 'ph_relationship') {
      return String(value).toLowerCase().replace(/\s+/g, '');
    }

    if (fieldName === 'state' || fieldName === 'ph_state') {
      return String(value).toUpperCase();
    }

    return String(value).trim();
  }

  private updateUIWithDiscrepancies(): void {
    if (!this.payload?.properties?.items?.value) return;

    this.payload.properties.items.value.forEach((container: any) => {
      if (container.items && Array.isArray(container.items)) {
        container.items.forEach((insuranceEntry: any) => {
          const insuranceId = insuranceEntry.insurance_id;

          if (insuranceId && this.conflictingFields.has(String(insuranceId))) {
            const conflicts = this.conflictingFields.get(String(insuranceId));
            const details = this.conflictDetails.get(String(insuranceId));

            if (insuranceEntry.items && Array.isArray(insuranceEntry.items)) {
              insuranceEntry.items.forEach((group: any) => {
                if (group.items && Array.isArray(group.items)) {
                  group.items.forEach((field: any) => {
                    if (field.name && conflicts?.has(field.name)) {
                      const conflictDetail = details?.get(field.name);
                      if (conflictDetail) {
                        field.hasConflict = true;
                        field.conflictTooltip = `PatientNow: ${conflictDetail.current}`;
                      }
                    }
                  });
                }
              });
            }
          }
        });
      }
    });

    this._cd.markForCheck();
    this._cd.detectChanges();
  }
  /**
   * Key of one insurance field for the amendment indicator: container name, entry, group and
   * field name, as the amendment field extractor builds it.
   */
  public getAmendmentFieldKey(
    container: { name?: string } | null | undefined,
    entryIndex: number,
    groupIndex: number,
    field: { name?: string } | null | undefined,
  ): string {
    return buildInsuranceFieldKey(container?.name ?? '', entryIndex, groupIndex, field?.name ?? '');
  }
}
